import { Cloud, GitBranch, SquaresFour, Buildings } from '@phosphor-icons/react'
import TemplateInput from '@/components/ui/TemplateInput'
import { Field } from '@/components/ui/Field'
import type { WorkflowNodeData, CrmProvider } from '@/types/workflow'

interface CrmSyncConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import {
  inputClass,
  inputStyle,
  sectionClass,
  infoBannerClass,
  checkboxInputClass,
  checkboxLabelClass,
} from './_kit'
const CRM_OPTIONS: { value: CrmProvider; label: string; icon: typeof Cloud }[] = [
  { value: 'hubspot', label: 'HubSpot', icon: SquaresFour },
  { value: 'pipedrive', label: 'Pipedrive', icon: GitBranch },
  { value: 'salesforce', label: 'Salesforce', icon: Cloud },
  { value: 'zoho', label: 'Zoho CRM', icon: Buildings },
]

const DEFAULT_CONFIG = {
  provider: 'hubspot' as CrmProvider,
  sync_contact: true,
  create_deal: false,
  deal_name_template: '',
  deal_stage: '',
  field_mapping: {} as Record<string, string>,
}

export default function CrmSyncConfig({ data, updateField }: CrmSyncConfigProps) {
  const config = { ...DEFAULT_CONFIG, ...data.crm_sync_config }

  const updateConfig = (updates: Partial<typeof config>) => {
    updateField('crm_sync_config', { ...config, ...updates })
  }

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            className={inputClass} style={inputStyle}
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="CRM Sync"
          />
        )}
      </Field>

      {/* CRM Provider */}
      <Field label="CRM Provider" as="group">
        <div className="grid grid-cols-2 gap-2">
          {CRM_OPTIONS.map((crm) => {
            const Icon = crm.icon
            const isSelected = config.provider === crm.value
            return (
              <button
                key={crm.value}
                onClick={() => updateConfig({ provider: crm.value })}
                className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 transition-all duration-150 ${
                  isSelected
                    ? 'border-teal-500 bg-teal-500/10 text-teal-700 dark:text-teal-300'
                    : 'border-border/60 bg-card text-muted-foreground hover:border-border hover:bg-accent/30'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{crm.label}</span>
              </button>
            )
          })}
        </div>
      </Field>

      {/* Sync Contact — always on */}
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg">
        <div className="w-4 h-4 rounded bg-teal-500 flex items-center justify-center flex-shrink-0">
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <span className="text-sm text-foreground">Sync contact to {CRM_OPTIONS.find(c => c.value === config.provider)?.label}</span>
      </div>

      {/* Create Deal Toggle */}
      <div>
        <label className={checkboxLabelClass}>
          <input
            type="checkbox"
            checked={config.create_deal}
            onChange={(e) => updateConfig({ create_deal: e.target.checked })}
            className={checkboxInputClass}
          />
          <span className="text-sm font-medium">Also create a deal</span>
        </label>
      </div>

      {config.create_deal && (
        <>
          <Field
            label="Deal Name"
            hint={<>Use {'{{contact.field}}'} for dynamic values</>}
          >
            {(f) => (
              <TemplateInput
                {...f}
                value={config.deal_name_template}
                onChange={(v) => updateConfig({ deal_name_template: v })}
                placeholder="{{contact.company_name}} — Outreach"
                rows={1}
              />
            )}
          </Field>
          <Field label="Deal Stage">
            {(f) => (
              <input
                {...f}
                type="text"
                className={inputClass} style={inputStyle}
                value={config.deal_stage}
                onChange={(e) => updateConfig({ deal_stage: e.target.value })}
                placeholder="e.g. Prospecting, Qualified Lead"
              />
            )}
          </Field>
        </>
      )}

      <div className={infoBannerClass}>
        <p className="text-xs">
          Contact fields (name, email, company, job title, phone) are synced automatically from your contact data. Configure your CRM API key in Settings → Integrations.
        </p>
      </div>
    </div>
  )
}
